
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Plus, 
  Search, 
  Calendar, 
  Users,
  Settings,
  Zap,
  StickyNote,
  ChevronDown
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useScreenSize } from '@/hooks/useScreenSize';
import { useState } from 'react';
import { CreateBoardDialog } from '@/components/CreateBoardDialog';
import { useBoardContext } from '@/contexts/BoardContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';

export const QuickActions = () => {
  const navigate = useNavigate();
  const { isMobile, isTablet } = useScreenSize();
  const [showCreateBoard, setShowCreateBoard] = useState(false);
  const { boards, currentBoardId, setCurrentBoardId } = useBoardContext();
  const [selectedBoardId, setSelectedBoardId] = useState<string | null>(currentBoardId);

  // Use first board as default if no selection and boards exist
  const effectiveSelectedBoardId = selectedBoardId || boards?.[0]?.id || null;
  const selectedBoard = boards?.find(board => board.id === effectiveSelectedBoardId);

  const actions = [
    {
      title: 'New Board',
      description: 'Create a new project board',
      icon: Plus,
      onClick: () => setShowCreateBoard(true),
      color: 'text-blue-600',
      bgColor: 'bg-blue-50 dark:bg-blue-950',
      needsBoard: false
    },
    {
      title: 'Notes',
      description: 'Quick notes and reminders',
      icon: StickyNote,
      onClick: () => navigate('/notes'),
      color: 'text-orange-600',
      bgColor: 'bg-orange-50 dark:bg-orange-950',
      needsBoard: false
    },
    {
      title: 'Calendar',
      description: selectedBoard ? `View ${selectedBoard.title} deadlines` : 'View deadlines and schedule',
      icon: Calendar,
      onClick: () => {
        if (effectiveSelectedBoardId) {
          setCurrentBoardId(effectiveSelectedBoardId);
          navigate('/calendar');
        } else {
          navigate('/calendar');
        }
      },
      color: 'text-green-600',
      bgColor: 'bg-green-50 dark:bg-green-950',
      needsBoard: true
    },
    {
      title: 'Settings',
      description: selectedBoard ? `Manage ${selectedBoard.title} settings` : 'Manage board settings',
      icon: Settings,
      onClick: () => {
        if (effectiveSelectedBoardId) {
          setCurrentBoardId(effectiveSelectedBoardId);
          navigate('/board-settings');
        } else {
          navigate('/board-settings');
        }
      },
      color: 'text-purple-600',
      bgColor: 'bg-purple-50 dark:bg-purple-950',
      needsBoard: true
    }
  ];

  if (isMobile) {
    return (
      <>
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Quick Actions
              </CardTitle>
              {boards && boards.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 text-xs">
                      {selectedBoard ? selectedBoard.title.slice(0, 12) + (selectedBoard.title.length > 12 ? '...' : '') : 'Select Board'}
                      <ChevronDown className="h-3 w-3 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {boards.map((board) => (
                      <DropdownMenuItem
                        key={board.id}
                        onClick={() => setSelectedBoardId(board.id)}
                        className="flex items-center justify-between"
                      >
                        <span className="truncate">{board.title}</span>
                        {board.id === effectiveSelectedBoardId && (
                          <Badge variant="secondary" className="text-xs">Current</Badge>
                        )}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {actions.map((action, index) => {
                const Icon = action.icon;
                const isDisabled = action.needsBoard && !effectiveSelectedBoardId;
                return (
                  <Button
                    key={index}
                    variant="outline"
                    className="h-20 flex-col gap-2 text-center"
                    onClick={action.onClick}
                    disabled={isDisabled}
                  >
                    <div className={`${action.bgColor} p-2 rounded-full ${isDisabled ? 'opacity-50' : ''}`}>
                      <Icon className={`${action.color} h-5 w-5`} />
                    </div>
                    <span className="text-xs font-medium">{action.title}</span>
                  </Button>
                );
              })}
            </div>
          </CardContent>
        </Card>
        <CreateBoardDialog open={showCreateBoard} onOpenChange={setShowCreateBoard} />
      </>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Quick Actions
            </CardTitle>
            {boards && boards.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    {selectedBoard ? selectedBoard.title : 'Select Board'}
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  {boards.map((board) => (
                    <DropdownMenuItem
                      key={board.id}
                      onClick={() => setSelectedBoardId(board.id)}
                      className="flex items-center justify-between"
                    >
                      <span className="truncate">{board.title}</span>
                      {board.id === effectiveSelectedBoardId && (
                        <Badge variant="secondary" className="text-xs">Current</Badge>
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className={`grid gap-4 ${isTablet ? 'grid-cols-2' : 'grid-cols-4'}`}>
            {actions.map((action, index) => {
              const Icon = action.icon;
              const isDisabled = action.needsBoard && !effectiveSelectedBoardId;
              return (
                <Button
                  key={index}
                  variant="outline"
                  className="h-24 flex-col gap-3 text-center hover:shadow-md transition-shadow"
                  onClick={action.onClick}
                  disabled={isDisabled}
                >
                  <div className={`${action.bgColor} p-3 rounded-full ${isDisabled ? 'opacity-50' : ''}`}>
                    <Icon className={`${action.color} h-6 w-6`} />
                  </div>
                  <div>
                    <div className="font-medium">{action.title}</div>
                    <div className="text-xs text-muted-foreground">{action.description}</div>
                  </div>
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>
      <CreateBoardDialog open={showCreateBoard} onOpenChange={setShowCreateBoard} />
    </>
  );
};
